// @cogo/template — deterministic, LLM-free instantiation compiler
//
// PURE leaf module: no node:fs, no network, no Date.now/Math.random, no ids
// minted. Given a TemplatePackage + a source AppBundle + a caller-supplied
// slotValues map (+ optional designSystem swap), produces a plain,
// ordinary-project AppBundle with every provided slot value written at its
// declared address(es). Omitted slots keep the package's sourceValue
// untouched (the deep-cloned source bundle already holds it).
//
// Fail-closed: `validateSlotValues` (Task 4) is the sole value-shape gate,
// called first — ANY failure aborts before the source bundle is even
// cloned, so a failed instantiation never returns a partial bundle.
//
// Address-write semantics are shared with canonical.ts's `applyMask` via
// resolve-address.ts's `applyAtAddress` (element = first preorder `id`
// match; app/record-list = JSON Pointer; datasource = find-by-key in
// app.json's datasources object map) — both consumers write through the
// same resolution logic instead of carrying separate copies. A declared
// slot address that fails to resolve throws `TypeError` from `applyAtAddress`;
// this module catches that per-address and converts it to a
// `slot-address-missing` TypedFailure so a ghost address (a declared slot
// whose address doesn't actually exist in `sourceBundle`) fails closed
// instead of throwing out of `instantiate`.

import type { AppBundle, Json, TemplatePackage, TypedFailure } from './types'
import { applyAtAddress } from './resolve-address'
import { validateSlotValues } from './validate-slots'

export interface InstantiateInput {
  pkg: TemplatePackage
  sourceBundle: AppBundle
  slotValues: Record<string, Json>
  designSystem?: Json
}

export type InstantiateResult = { ok: true; bundle: AppBundle } | { ok: false; failures: TypedFailure[] }

function isPlainObject(value: unknown): value is { [k: string]: Json } {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Compiles `pkg` + `sourceBundle` + caller-supplied `slotValues` (and an
 * optional `designSystem` swap) into a plain, ordinary AppBundle — no LLM,
 * no ids minted, no timestamps, fully deterministic for identical inputs.
 *
 * Fails closed: value-shape errors (Task 4's `validateSlotValues`) and an
 * unauthorized/invalid design-system swap both abort BEFORE the source
 * bundle is cloned, so a failed call never returns a partial bundle. The
 * input `sourceBundle` (and its nested values) is never mutated — a fresh
 * `structuredClone` is written into on success.
 */
export function instantiate(input: InstantiateInput): InstantiateResult {
  const { pkg, sourceBundle, slotValues, designSystem } = input

  const valueFailures = validateSlotValues(pkg, slotValues)
  if (valueFailures.length > 0) {
    return { ok: false, failures: valueFailures }
  }

  if (designSystem !== undefined) {
    if (pkg.designBinding.rebinding !== 'allowed') {
      return {
        ok: false,
        failures: [
          {
            code: 'design-rebinding-not-allowed',
            message: `design system rebinding is not allowed for this package (rebinding="${pkg.designBinding.rebinding}")`,
          },
        ],
      }
    }
    if (!isPlainObject(designSystem)) {
      return {
        ok: false,
        failures: [{ code: 'design-release-invalid', message: 'provided designSystem is not a JSON object' }],
      }
    }
  }

  const bundle: AppBundle = structuredClone(sourceBundle)
  const bySlotId = new Map(pkg.slots.map((s) => [s.slotId, s]))
  const addressFailures: TypedFailure[] = []

  for (const slotId of Object.keys(slotValues)) {
    const slot = bySlotId.get(slotId)
    // Unreachable in practice: validateSlotValues above already rejects any
    // key that isn't a declared slot id (code "slot-value-invalid").
    if (slot === undefined) continue
    const value = slotValues[slotId]
    const addresses = Array.isArray(slot.address) ? slot.address : [slot.address]
    for (const address of addresses) {
      // A declared slot's address should always resolve — validateSlotDecls
      // (authoring-side) is supposed to catch a ghost address before a
      // package is ever published. But instantiate must not trust that gate
      // blindly: if it ever runs against a stale/hand-edited package whose
      // address no longer resolves in `sourceBundle`, applyAtAddress throws
      // a TypeError. Catch it here and convert it to a typed failure instead
      // of letting it escape as an uncaught throw.
      try {
        applyAtAddress(bundle.files, address, value)
      } catch (err) {
        addressFailures.push({
          code: 'slot-address-missing',
          slotId,
          message: err instanceof Error ? err.message : String(err),
        })
      }
    }
  }

  if (addressFailures.length > 0) {
    // Fail closed: the mutated `bundle` local is discarded, never returned —
    // same "no partial bundle" guarantee as the value-shape/design-system
    // failure paths above.
    return { ok: false, failures: addressFailures }
  }

  if (designSystem !== undefined) {
    // Deep-cloned, not assigned by reference — the returned bundle must stay
    // independent of the caller's designSystem object (mutating it after
    // instantiate returns must not affect the result).
    bundle.files[pkg.designBinding.file] = structuredClone(designSystem)
  }

  return { ok: true, bundle }
}
